import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

export const CANCEL_REASON_OPTIONS = [
  { value: 'students_absent', label: 'Students absent / no one joined' },
  { value: 'teacher_absent', label: 'Teacher absent' },
  { value: 'holiday', label: 'Holiday (paid class, no makeup)' },
  { value: 'other', label: 'Other (enter reason)' },
];

export function buildCancelPayload(cancelReason, otherText) {
  if (cancelReason === 'other') {
    const text = String(otherText || '').trim();
    if (!text) return { error: 'Please enter a reason for cancellation' };
    return { cancelReason, reason: text };
  }
  return { cancelReason };
}

export default function CancelClassDialog({ open, onClose, onConfirm, loading }) {
  const [cancelReason, setCancelReason] = useState('students_absent');
  const [otherText, setOtherText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setCancelReason('students_absent');
    setOtherText('');
    setError('');
  }, [open]);

  const handleConfirm = () => {
    const payload = buildCancelPayload(cancelReason, otherText);
    if (payload.error) {
      setError(payload.error);
      return;
    }
    onConfirm(payload);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cancel class</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use when the class did not happen. Attendance, homework, class log, and completion will not be required.
          Holiday is a paid class day (including makeup) and does not create another makeup.
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="cancel-reason-label">Reason</InputLabel>
          <Select
            labelId="cancel-reason-label"
            label="Reason"
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              setError('');
            }}
          >
            {CANCEL_REASON_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {cancelReason === 'other' && (
          <TextField
            fullWidth
            size="small"
            label="Reason details"
            multiline
            minRows={2}
            value={otherText}
            onChange={(e) => {
              setOtherText(e.target.value);
              setError('');
            }}
            placeholder="Describe why this class was cancelled"
          />
        )}
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Back
        </Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={loading}>
          {loading ? 'Cancelling…' : 'Cancel class'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
